/**
 * KAN-648: поля видеозаписи аккаунта: ссылка на видео и файл (video/*, до 200 МБ).
 * Используются в мастере подключения и в окне видео карточки аккаунта.
 */
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  VIDEO_ACCEPT,
  validateVideoFile,
  validateVideoUrl,
  type VideoDraft,
} from '@/lib/accountVideo';

interface Props {
  value: VideoDraft;
  onChange: (next: VideoDraft) => void;
  disabled?: boolean;
  /** Префикс id и data-testid (в одном окне может быть несколько форм). */
  idPrefix?: string;
}

export function AccountVideoFields({
  value,
  onChange,
  disabled,
  idPrefix = 'account-video',
}: Props): JSX.Element {
  const [fileError, setFileError] = useState<string | null>(null);
  const urlError = validateVideoUrl(value.url);

  const pickFile = (file: File | null) => {
    if (!file) {
      setFileError(null);
      onChange({ ...value, file: null });
      return;
    }
    const problem = validateVideoFile(file);
    setFileError(problem);
    onChange({ ...value, file: problem ? null : file });
  };

  return (
    <div className="space-y-2" data-testid={`${idPrefix}-fields`}>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-url`}>Ссылка на видео</Label>
        <Input
          id={`${idPrefix}-url`}
          data-testid={`${idPrefix}-url`}
          type="url"
          placeholder="https://..."
          value={value.url}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, url: e.target.value })}
        />
        {urlError && (
          <p className="text-xs text-red-600" data-testid={`${idPrefix}-url-error`}>
            {urlError}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-file`}>Файл видео (до 200 МБ)</Label>
        <Input
          id={`${idPrefix}-file`}
          data-testid={`${idPrefix}-file`}
          type="file"
          accept={VIDEO_ACCEPT}
          disabled={disabled}
          onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
        />
        {fileError && (
          <p className="text-xs text-red-600" data-testid={`${idPrefix}-file-error`}>
            {fileError}
          </p>
        )}
      </div>
    </div>
  );
}
